import { Arr } from '@deqo/foundation';
import { pascalCase } from 'change-case';
import React, { useCallback, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, Input, remInPx } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { useResolve } from '../../Container';
import { Copy, DisableFrame, Flexer, Spacer, Title } from '../../dashboard/elements/LayoutElements';
import { pollStart, pollToButton } from '../../packages/polling/PollCommands';
import { PollType } from '../../packages/polling/PollTypes';

const XScrollbars = Scrollbars as any;

const placeholders = {
    single: [
        'If a tin whistle is made out of tin, is a fog horn made out of fog?',
        'Does fuzzy logic tickle?',
        'If I try to fail, but I succeed, which one did I actually do?',
        'If your shirt isn\'t tucked into your pants, are your pants tucked into your shirt?'
    ],
    multi: [
        'What was the best thing before sliced bread?',
        'Why does it take so long for quicksand to work?',
        'How do towels get dirty if you only ever use them when you\'re clean?',
        'Why did Cinderella\'s shoe fall off if it fit her perfectly?',
    ],
    scale: [
        'Pick a number between 0 and 10:'
    ],
};

export const SetupPollQuestionSlide: LFC<{ type: PollType, onAction: (path: string) => void }> = ({ type, onAction }) => {

    const example = useMemo(() => Arr.random(placeholders[type]), [type]);
    const defaultAnswers = type === 'scale' ? ['0', '10'] : ['Yes', 'No', ''];

    const dispatch = useResolve(CommandDispatcher);
    const [question, setQuestion] = useState('');
    const [options, setAnswers] = useState(defaultAnswers);
    const valid = useMemo(() => !!question.trim() && options.map(x => x.trim()).filter(x => !!x).length >= 2, [question, options]);

    const onSet = useCallback((i: number, value: string) => {
        const next = Object.assign(options.slice(), { [i]: value })
            .map(x => type === 'scale' ? (parseFloat(x) || 0).toString() : x)
            .filter(x => !!x)
            .concat(type === 'scale' ? [] : ['']);
        setAnswers(next.slice(0, 10));
    }, [type, options]);

    const onBack = useCallback(() => {
        onAction('menu/poll');
    }, [onAction]);

    const onStart = useCallback(() => {
        dispatch(pollStart, [type, question.trim(), ...options.map(x => x.trim()).filter(x => !!x)]);
        onAction('exit');
    }, [onAction, type, question, options]);

    const onSave = useCallback(() => {
        dispatch(pollToButton, [type, question.trim(), ...options.map(x => x.trim()).filter(x => !!x)]);
        onAction('exit');
    }, [onAction, type, question, options]);

    return <Flexer height="100%" padding="2 3">
        <Flexer margin=".5 0" gap=".375" flex="row" center>
            <Icon src={`deqo://icons/meeting/poll-${type}`} size="2" />
            <Title size="lg" flush>
                {type === 'single' && 'Single Choice Poll'}
                {type === 'multi' && 'Muliple Choice Poll'}
                {type === 'scale' && 'Number Scale Poll'}
            </Title>
        </Flexer>
        <Flexer margin=".5 0" color={Colors.text}>
            <Input
                label="Question:"
                maxLength="140"
                placeholder={`e.g. ${example}`}
                multiline
                value={question}
                onChange={setQuestion} />
            <Spacer f={1} />
            {type !== 'scale' && <>
                <Input.Label>Answers:</Input.Label>
                <Flexer margin="0 -1" >
                    <XScrollbars autoHeight autoHeightMax={remInPx(18.375)}>
                        <Flexer padding=".125 1">
                            {options.map((v, i) =>
                                <Flexer key={i} padding="0 0 .5 0">
                                    <Input maxLength="70" placeholder="Add another..." value={v} onChange={n => onSet(i, n)} />
                                </Flexer>
                            )}
                        </Flexer>
                    </XScrollbars>
                </Flexer>
            </>}
            {type === 'scale' && <>
                <Title>From:</Title>
                <Flexer padding="0 0 .5 0">
                    <Input placeholder="Min" value={options[0]} onChange={n => onSet(0, n)} />
                </Flexer>
                <Title>To:</Title>
                <Flexer padding="0 0 .5 0">
                    <Input placeholder="Max" value={options[1]} onChange={n => onSet(1, n)} />
                </Flexer>
            </>}
            <Flexer margin=".5 0" flex="row" align="center" justify="space-between">
                <Flexer>
                    <DropButton padding=".125 .375" onClick={onBack}>
                        <Icon color={Colors.primaryText} size="1.5" src="deqo://icons/hero/arrow-sm-left" />
                        <Copy color={Colors.primaryText} flush>Go Back</Copy>
                    </DropButton>
                </Flexer>
                <DisableFrame disabled={!valid}>
                    <Flexer flex="row" gap="1">
                        <DropButton data-action={`editor.hostMenu.polls.save${pascalCase(type)}`} padding=".125 .375" onClick={onSave}>
                            <Copy color={Colors.primaryText} flush>Save As Button</Copy>
                        </DropButton>
                        <DropButton data-action={`editor.hostMenu.polls.start${pascalCase(type)}`} padding=".125 .375" onClick={onStart}>
                            <Copy color={Colors.primaryText} flush>Start Now</Copy>
                        </DropButton>
                    </Flexer>
                </DisableFrame>
            </Flexer>
        </Flexer>
    </Flexer>;
};
